import React, { useState } from 'react';
import {
  formatShortWhen,
  formatWhatsappLink,
  formatInstagramLink,
  formatMailLink,
} from '../../utils/adminLeadsHelpers';

// Los únicos tres valores que guarda el backend para el paso alcanzado
// (ver formularioIncompletoService.js — pasoMaximo sube de 0 a 2 y no baja).
const ETIQUETA_PASO = {
  0: 'Dejó los datos y se fue',
  1: 'Respondió la cualificación',
  2: 'Llegó al precio y no envió',
};

// Las respuestas de un incompleto llegan como el slug crudo del radio button
// del formulario (ver FormPage.js): todavía no pasaron por el envío, que es
// donde el backend las traduce al texto largo que sí guarda Hoja 1. Se
// traducen acá para que la card se lea sin tener que recordar códigos.
const EXPERIENCIA_LABEL = {
  'menos-6-meses': 'Menos de 6 meses entrenando',
  '6-meses-2-años': 'Entre 6 meses y 2 años entrenando',
  'mas-2-años': 'Más de 2 años entrenando',
};

const SITUACION_LABEL = {
  'no-refleja-esfuerzo': 'Entrena hace tiempo, su físico no refleja el esfuerzo',
  estancado: 'Estancado, no sabe qué variable ajustar',
  'nivel-competitivo': 'Quiere nivel competitivo / avanzado',
  'rutina-nueva': 'Solo busca una rutina nueva',
};

const URGENCIA_LABEL = {
  'necesito-ahora': 'Quiere empezar ahora',
  'proximas-semanas': 'Quiere empezar pronto',
  'sin-prisa': 'Sin prisa por empezar',
  'no-interesado': 'No está interesado',
};

const COACH_PREFERENCE_LABEL = {
  agustin: 'Agustín · Head Coach',
  equipo_alvaro: 'Álvaro · Team Coach',
};

const BUDGET_CONFIRM_LABEL = {
  si_listo: 'Sí, tiene el presupuesto y está listo/a',
  organizaria: 'Vería cómo organizar el presupuesto',
  no_posible: 'Hoy no le acomoda económicamente',
};

const FALLBACK_EQUIPO_LABEL = {
  si: 'Sí, le interesa pasar a equipo AU',
  esperar: 'Prefiere esperar para ir con Agustín',
  ninguna: 'No quiere ninguna de las dos opciones',
};

// Un valor sin traducción conocida se muestra tal cual: mejor el código crudo
// que esconder una respuesta real porque el mapa de arriba quedó corto.
const traducir = (mapa, valor) => {
  const v = String(valor || '').trim();
  return v ? (mapa[v] || v) : '';
};

/**
 * Card de alguien que empezó el formulario y no lo envió. Mucho más simple que
 * LeadEntry: no hay bot, ni resultado de llamada, ni historial — solo lo que
 * alcanzó a contestar antes de irse.
 */
const IncompletoEntry = ({ incompleto, onContactado }) => {
  const [submitting, setSubmitting] = useState(false);

  const wa = formatWhatsappLink(incompleto.whatsapp);
  const ig = formatInstagramLink(incompleto.instagram);
  const mail = formatMailLink(incompleto.email);
  const etiquetaPaso = ETIQUETA_PASO[incompleto.pasoMaximo] ?? 'Paso sin identificar';
  const yaContactado = Boolean(incompleto.contactadoPor);

  // Un campo vacío se omite en vez de mostrarse en blanco: ensucia la lectura
  // de quien tiene que decidir a quién llamar.
  const respuestas = [
    ['Experiencia', traducir(EXPERIENCIA_LABEL, incompleto.experiencia)],
    ['Situación', traducir(SITUACION_LABEL, incompleto.situacion)],
    ['Urgencia', traducir(URGENCIA_LABEL, incompleto.urgencia)],
    ['Freno', incompleto.frenoActual ? `“${incompleto.frenoActual}”` : ''],
    ['Coach preferido', traducir(COACH_PREFERENCE_LABEL, incompleto.coachPreference)],
    ['Presupuesto', traducir(BUDGET_CONFIRM_LABEL, incompleto.budgetConfirm)],
    ['Alternativa equipo', traducir(FALLBACK_EQUIPO_LABEL, incompleto.fallbackEquipo)],
  ].filter(([, valor]) => Boolean(valor));

  const handleContactado = async () => {
    if (submitting || yaContactado) return;
    setSubmitting(true);
    try {
      await onContactado(incompleto.email);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="au-lead">
      <div className="au-lead-meta">
        <div className="when">{formatShortWhen(incompleto.ultimaActividad)}</div>
        <div className="seg">{etiquetaPaso}</div>
      </div>

      <div className="au-lead-body">
        <div className="name">
          <span>{incompleto.nombre || '[SIN NOMBRE]'}</span>
        </div>

        {respuestas.length > 0 && (
          <div className="au-lead-fields">
            {respuestas.map(([etiqueta, valor]) => (
              <div className="au-lead-field" key={etiqueta}>
                <div className="au-lead-field-label">{etiqueta}</div>
                <div className="au-lead-field-value">{valor}</div>
              </div>
            ))}
          </div>
        )}

        <div className="au-lead-section" />

        <div className="au-lead-contact">
          <div className="au-lead-contact-label">WhatsApp</div>
          <div className={`au-lead-contact-value ${wa ? '' : 'empty'}`}>
            {wa ? <a href={wa} target="_blank" rel="noreferrer">{incompleto.whatsapp}</a> : '(sin dato)'}
          </div>
          {wa
            ? <a className="au-lead-contact-action" href={wa} target="_blank" rel="noreferrer">&gt;&gt; abrir</a>
            : <span className="au-lead-contact-action disabled">&gt;&gt; abrir</span>}
        </div>

        <div className="au-lead-contact">
          <div className="au-lead-contact-label">Email</div>
          <div className={`au-lead-contact-value ${mail ? '' : 'empty'}`}>
            {mail ? <a href={mail}>{incompleto.email}</a> : '(sin dato)'}
          </div>
          {mail
            ? <a className="au-lead-contact-action" href={mail}>&gt;&gt; enviar mail</a>
            : <span className="au-lead-contact-action disabled">&gt;&gt; enviar mail</span>}
        </div>

        <div className="au-lead-contact">
          <div className="au-lead-contact-label">Instagram</div>
          <div className={`au-lead-contact-value ${ig ? '' : 'empty'}`}>
            {ig ? <a href={ig} target="_blank" rel="noreferrer">{incompleto.instagram}</a> : '(sin dato)'}
          </div>
          {ig
            ? <a className="au-lead-contact-action" href={ig} target="_blank" rel="noreferrer">&gt;&gt; abrir IG</a>
            : <span className="au-lead-contact-action disabled">&gt;&gt; abrir IG</span>}
        </div>

        {incompleto.correoDeOrigen && (
          <div className="au-lead-field">
            <div className="au-lead-field-label">Correo</div>
            <div className="au-lead-field-value">
              {incompleto.correoDeOrigen.etiqueta}
              {incompleto.correoDeOrigen.asunto && (
                <div className="muted">&ldquo;{incompleto.correoDeOrigen.asunto}&rdquo;</div>
              )}
            </div>
          </div>
        )}

        {yaContactado ? (
          <span className="au-lead-status-badge">
            CONTACTADO &#10003; {incompleto.contactadoPor}
            {incompleto.contactadoEl ? ` · ${formatShortWhen(incompleto.contactadoEl)}` : ''}
          </span>
        ) : (
          <button
            type="button"
            className="au-lead-mark"
            onClick={handleContactado}
            disabled={submitting}
          >
            {submitting ? 'Guardando…' : 'Contactado'}
          </button>
        )}
      </div>
    </div>
  );
};

export default IncompletoEntry;
